import {
  RangeValue,
  DateRange,
  EnglishFilter,
  Coordinates,
  SortOrder,
} from "types";

export const buildFilters = (
  filters: {
    durationDay: RangeValue;
    estimatedTotalCourseFee: RangeValue;
    startDate: DateRange;
    levels: string[];
    universities: string[];
    fieldsOfStudy: string[];
    modesOfStudy: string[];
    country: string[];
  },
  useStringFormat: boolean = true
): { and: (string | object)[] } | undefined => {
  const filterConditions: (string | object)[] = [];

  // Handle Range filters directly from the flattened structure
  if (filters.durationDay?.gte != null || filters.durationDay?.lte != null) {
    const rangeFilter = {
      durationDay: {
        gte: filters.durationDay?.gte ?? undefined,
        lte: filters.durationDay?.lte ?? undefined,
      },
    };

    filterConditions.push(
      useStringFormat ? JSON.stringify(rangeFilter) : rangeFilter
    );
  }

  if (
    filters.estimatedTotalCourseFee?.gte != null ||
    filters.estimatedTotalCourseFee?.lte != null
  ) {
    const rangeFilter = {
      estimatedTotalCourseFee: {
        gte: filters.estimatedTotalCourseFee?.gte ?? undefined,
        lte: filters.estimatedTotalCourseFee?.lte ?? undefined,
      },
    };

    filterConditions.push(
      useStringFormat ? JSON.stringify(rangeFilter) : rangeFilter
    );
  }

  if (filters.startDate?.gte != null || filters.startDate?.lte != null) {
    const rangeFilter = {
      startDate: {
        gte: filters.startDate?.gte ?? undefined,
        lte: filters.startDate?.lte ?? undefined,
      },
    };

    filterConditions.push(
      useStringFormat ? JSON.stringify(rangeFilter) : rangeFilter
    );
  }

  // Handle Categorical filters
  if (filters.levels.length > 0) {
    const orFilter = {
      or: filters.levels.map((level) => ({ level: { eq: level } })),
    };

    filterConditions.push(
      useStringFormat ? JSON.stringify(orFilter) : orFilter
    );
  }

  if (filters.universities.length > 0) {
    const orFilter = {
      or: filters.universities.map((university) => ({
        institutionName: { eq: university },
      })),
    };

    filterConditions.push(
      useStringFormat ? JSON.stringify(orFilter) : orFilter
    );
  }

  if (filters.fieldsOfStudy.length > 0) {
    const orFilter = {
      or: filters.fieldsOfStudy.map((field) => ({
        fieldOfStudy: { eq: field },
      })),
    };

    filterConditions.push(
      useStringFormat ? JSON.stringify(orFilter) : orFilter
    );
  }

  if (filters.modesOfStudy?.length > 0) {
    const validModes = filters.modesOfStudy.filter((mode): mode is string =>
      Boolean(mode)
    );
    if (validModes.length > 0) {
      const orFilter = {
        or: validModes.map((mode) => ({ modeOfStudy: { eq: mode } })),
      };

      filterConditions.push(
        useStringFormat ? JSON.stringify(orFilter) : orFilter
      );
    }
  }

  if (filters.country?.length > 0) {
    const validModes = filters.country.filter((mode): mode is string =>
      Boolean(mode)
    );
    if (validModes.length > 0) {
      const orFilter = {
        or: validModes.map((mode) => ({ country: { eq: mode } })),
      };

      filterConditions.push(
        useStringFormat ? JSON.stringify(orFilter) : orFilter
      );
    }
  }

  return filterConditions.length > 0 ? { and: filterConditions } : undefined;
};

const getEnglishFilter = (
  testType: string,
  englishFilter: EnglishFilter | undefined
) => {
  if (englishFilter && testType in englishFilter) {
    return {
      [testType]: englishFilter[testType as keyof EnglishFilter],
    };
  }
  return null;
};

const getLocationFilter = (coordinates: Coordinates) => {
  const { lat, lon, km } = coordinates;
  return lat !== null && lon !== null && km !== null
    ? [{ lat, lon, km }]
    : null;
};

const getSortFilter = (sort: SortOrder) => {
  return sort === SortOrder.ASCENDING || sort === SortOrder.DESCENDING
    ? { direction: sort, field: "estimatedTotalCourseFee" }
    : null;
};

export const handleQueryVariable = (
  query: string,
  limit: number,
  filters: {
    durationDay: RangeValue;
    estimatedTotalCourseFee: RangeValue;
    startDate: DateRange;
    levels: string[];
    universities: string[];
    fieldsOfStudy: string[];
    modesOfStudy: string[];
    country: string[];
  },
  englishProficiencyFilter: EnglishFilter | undefined,
  testType: string,
  coordinates: Coordinates,
  sort: SortOrder,
  token: string | null
) => {
  const combinedFilters = buildFilters(filters);
  const englishFilter = getEnglishFilter(testType, englishProficiencyFilter);
  const locationFilter = getLocationFilter(coordinates);
  const sortFilter = getSortFilter(sort);

  const variables: any = {
    query: query,
    aggregates: [
      { name: "level", type: "terms", field: "level" },
      { name: "institutionName", type: "terms", field: "institutionName" },
      { name: "fieldOfStudy", type: "terms", field: "fieldOfStudy" },
      { name: "modeOfStudy", type: "terms", field: "modeOfStudy" },
      { name: "durationDayMax", type: "max", field: "durationDay" },
      { name: "durationDayMin", type: "min", field: "durationDay" },
      {
        name: "estimatedTotalCourseFeeMax",
        type: "max",
        field: "estimatedTotalCourseFee",
      },
      {
        name: "estimatedTotalCourseFeeMin",
        type: "min",
        field: "estimatedTotalCourseFee",
      },
      { name: "country", type: "terms", field: "country" },
    ],
    limit: limit,
  };

  if (token) {
    variables.nextToken = token;
  }

  if (combinedFilters) {
    variables.filter = combinedFilters;
  }

  if (englishFilter) {
    variables.englishFilter = englishFilter;
  }

  if (locationFilter && locationFilter.length > 0) {
    const firstLocation = locationFilter[0];
    variables.location = {
      lat: firstLocation.lat,
      lon: firstLocation.lon,
      km: firstLocation.km,
    };
  }

  if (sortFilter) {
    variables.sort = [sortFilter];
  }

  return variables;
};
